import { AlenDevice, AlenDeviceStatus } from "src/interfaces/IAlenDevice";
import {
  AlenProductConfigType,
  FilterFunction,
  LightFunction,
  LockFunction,
  ModeFunction,
  PowerFunction,
  SleepFunction,
  SpeedFunction,
  StandardFunction,
} from ".";
import { SEPARATE_MODE_AND_SLEEP_PRODUCTS } from "./shadowConfig";

export type AlenShadow = {
  Power?: PowerStandardValues;
  Sleep?: SleepStandardValues;
  Mode?: ModeStandardValues;
  Speed?: SpeedStandardValues;
  filter_life?: string;
  Lock?: LockStandardValues;
  Light?: LightStandardValues;
};

export type ShadowField = keyof AlenShadow;

export const ShadowFields: ShadowField[] = [
  "Power",
  "Sleep",
  "Mode",
  "Speed",
  "filter_life",
  "Lock",
  "Light",
];

export type PowerStandardValues = "On" | "Off";
export type SleepStandardValues = "On" | "Off" | "N/A";
export type ModeStandardValues = "Auto" | "Manual" | "Sleep" | "N/A";
export type SpeedStandardValues = "1" | "2" | "3" | "4" | "T";
export type LockStandardValues = "Lock" | "Unlock";
export type LightStandardValues = "Low" | "High" | "Auto" | "Sleep";

export const flattenProductConfig = (config: AlenProductConfigType) => {
  const { products } = config;
  const productsConfig = Object.keys(products).map((key) => products[key]);
  return productsConfig.flat();
};

export const getAlenProductFunctions = (
  productId: string,
  alenProductConfig: AlenProductConfigType
) => {
  const productsConfig = flattenProductConfig(alenProductConfig);
  const productConfig = productsConfig.find(
    (pc) => pc.productInfo.pid === productId
  );
  if (!productConfig) {
    throw new Error(`Product config not found for product id: ${productId}`);
  }
  const { standardFunctions, customFunctions } =
    productConfig.functionDefinition;
  return [...standardFunctions, ...(customFunctions ?? [])];
};

export const findProductFunctions = <T extends StandardFunction>(
  productFunctions: StandardFunction[],
  standardName: string
) => {
  const productFunction = productFunctions.find(
    (f) => f.standardName === standardName
  ) as T | undefined;
  if (!productFunction) {
    throw new Error(`${standardName} function not found`);
  }
  return productFunction;
};

export const getStatusValue = <T extends StandardFunction>(
  deviceStatus: AlenDeviceStatus[],
  productFunction: T
): string => {
  const { code } = productFunction;
  const powerStatus = deviceStatus.find(
    (s) => s.code.toLowerCase() === code.toLowerCase()
  );
  if (!powerStatus) {
    throw new Error(`Miss match value ${code}`);
  }
  return powerStatus.value;
};

export const getDevicePower = (
  deviceStatus: AlenDeviceStatus[],
  productFunctions: StandardFunction[]
) => {
  const powerFunction = findProductFunctions<PowerFunction>(
    productFunctions,
    "Power"
  );
  const powerValue = getStatusValue<PowerFunction>(deviceStatus, powerFunction);
  const standardValues = powerFunction.standardValues?.reduce(
    (acc, object) => ({ ...acc, ...object }),
    {}
  );
  return Object.entries(standardValues).find(
    ([, val]) => val === powerValue
  )?.[0] as PowerStandardValues | undefined;
};

export const getDeviceSleep = (
  deviceStatus: AlenDeviceStatus[],
  productFunctions: StandardFunction[]
) => {
  const sleepFunction = findProductFunctions<SleepFunction>(
    productFunctions,
    "Sleep"
  );
  const sleepValue = getStatusValue<SleepFunction>(deviceStatus, sleepFunction);
  const standardValues = sleepFunction.standardValues?.reduce(
    (acc, object) => ({ ...acc, ...object }),
    {}
  );
  return Object.entries(standardValues).find(
    ([, val]) => val === sleepValue
  )?.[0] as SleepStandardValues | undefined;
};

export const getDeviceMode = (
  deviceStatus: AlenDeviceStatus[],
  productFunctions: StandardFunction[]
) => {
  const modeFunction = findProductFunctions<ModeFunction>(
    productFunctions,
    "Mode"
  );
  const modeValue = getStatusValue<ModeFunction>(deviceStatus, modeFunction);
  const standardValues = modeFunction.standardValues?.reduce(
    (acc, object) => ({ ...acc, ...object }),
    {}
  );
  return Object.entries(standardValues).find(
    ([, val]) => val === modeValue
  )?.[0] as ModeStandardValues | undefined;
};

export const getDeviceSpeed = (
  deviceStatus: AlenDeviceStatus[],
  productFunctions: StandardFunction[]
) => {
  const speedFunction = findProductFunctions<SpeedFunction>(
    productFunctions,
    "Speed"
  );
  const speedValue = getStatusValue<SpeedFunction>(deviceStatus, speedFunction);
  const standardValues = speedFunction.standardValues?.reduce(
    (acc, object) => ({ ...acc, ...object }),
    {}
  );
  return Object.entries(standardValues).find(
    ([, val]) => val === speedValue
  )?.[0] as SpeedStandardValues | undefined;
};

export const getDeviceFilter = (
  deviceStatus: AlenDeviceStatus[],
  productFunctions: StandardFunction[]
) => {
  const filterFunction = findProductFunctions<FilterFunction>(
    productFunctions,
    "filter_life"
  );
  const filterValue = getStatusValue<FilterFunction>(
    deviceStatus,
    filterFunction
  );
  return filterValue;
};

export const getDeviceLock = (
  deviceStatus: AlenDeviceStatus[],
  productFunctions: StandardFunction[]
) => {
  const lockFunction = findProductFunctions<LockFunction>(
    productFunctions,
    "Lock"
  );
  const lockValue = getStatusValue<LockFunction>(deviceStatus, lockFunction);
  const standardValues = lockFunction.standardValues?.reduce(
    (acc, object) => ({ ...acc, ...object }),
    {}
  );
  return Object.entries(standardValues).find(
    ([, val]) => val === lockValue
  )?.[0] as LockStandardValues | undefined;
};

export const getDeviceLight = (
  deviceStatus: AlenDeviceStatus[],
  productFunctions: StandardFunction[]
) => {
  const lightFunction = findProductFunctions<LightFunction>(
    productFunctions,
    "Light"
  );
  const lightValue = getStatusValue<LightFunction>(deviceStatus, lightFunction);
  const standardValues = lightFunction.standardValues?.reduce(
    (acc, object) => ({ ...acc, ...object }),
    {}
  );
  return Object.entries(standardValues).find(
    ([, val]) => val === lightValue
  )?.[0] as LightStandardValues | undefined;
};

export const getAlenDeviceShadow = (
  alenDevice: AlenDevice,
  alenProductConfig: AlenProductConfigType
): AlenShadow => {
  const { product_id: productId, status } = alenDevice;

  let functions: StandardFunction[];
  try {
    functions = getAlenProductFunctions(productId, alenProductConfig);
  } catch (error) {
    return {};
  }

  const safeExecute = <T>(
    fn: () => T,
    fallback: T | undefined = undefined
  ): T | undefined => {
    try {
      return fn();
    } catch (error) {
      return fallback;
    }
  };
  let Mode = safeExecute(() => getDeviceMode(status, functions));
  let Sleep = safeExecute(() => getDeviceSleep(status, functions));
  if (!SEPARATE_MODE_AND_SLEEP_PRODUCTS.includes(alenDevice.product_id)) {
    if (Sleep === "On") {
      Mode = "N/A";
    }
    if (Mode === "Auto") {
      Sleep = "N/A";
    }
  }

  return {
    Power: safeExecute(() => getDevicePower(status, functions)),
    Sleep,
    Mode,
    Speed: safeExecute(() => getDeviceSpeed(status, functions)),
    filter_life: safeExecute(() => getDeviceFilter(status, functions)),
    Lock: safeExecute(() => getDeviceLock(status, functions)),
    Light: safeExecute(() => getDeviceLight(status, functions)),
  };
};
